import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle, AlertTriangle, Shield, Info } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'

interface ComplianceChecklistProps {
  compliance: AuditReport['compliance']
}

function sortChecks(checks: AuditReport['compliance']['checks']) {
  const order = (c: AuditReport['compliance']['checks'][0]) => {
    const isFail = c.status === 'fail'
    const isCritical = c.severity === 'critical'
    if (isFail && isCritical) return 0
    if (isFail && !isCritical) return 1
    if (!isFail && isCritical) return 2
    return 3
  }
  return [...checks].sort((a, b) => order(a) - order(b))
}

export function ComplianceChecklist({ compliance }: ComplianceChecklistProps) {
  const { t } = useTranslation()
  if (!compliance) return null

  const r = (key: string) => t(`amazonListing.report.${key}`)
  const checks = sortChecks(compliance.checks || [])

  return (
    <div className="space-y-4">
      <SectionCard title={r('complianceCheck')} score={compliance.score}>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
          <Info className="w-4 h-4 inline mr-1 -mt-0.5" />{r('complianceIntro')}
        </p>
        <div className="space-y-1">
          {checks.map((c, i) => (
            <div key={i} className={`flex items-start gap-2 py-2 px-2 sm:px-3 rounded-lg ${statusBg(c.status)}`}>
              <StatusIcon status={c.status} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-1">
                  <span className="text-sm text-neutral-800 dark:text-neutral-200">{c.detail}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${severityColor(c.severity)}`}>
                    {r(`severity${c.severity.charAt(0).toUpperCase()}${c.severity.slice(1)}`)}
                  </span>
                </div>
                {c.quote && <p className="text-xs text-neutral-500 mt-0.5 italic">&quot;{c.quote}&quot;</p>}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {compliance.health_claims_found?.length > 0 && (
        <HealthClaimsCard claims={compliance.health_claims_found} r={r} />
      )}

      {compliance.forbidden_words_found?.length > 0 && (
        <ForbiddenWordsCard words={compliance.forbidden_words_found} r={r} />
      )}
    </div>
  )
}

function HealthClaimsCard({ claims, r }: {
  claims: AuditReport['compliance']['health_claims_found']; r: (k: string) => string
}) {
  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 p-4">
      <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
        <Shield className="w-4 h-4 inline mr-1" />{r('healthClaims')}
      </h4>
      <div className="space-y-1">
        {claims.map((hc, i) => (
          <p key={i} className="text-sm text-yellow-700 dark:text-yellow-400">
            <span className="font-mono bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded">{hc.claim}</span>
            {' '}{r('in')} {hc.location} — {hc.suggestion}
          </p>
        ))}
      </div>
    </div>
  )
}

const LOCATION_ORDER = ['title', 'bullets', 'bullet_points', 'description']

function ForbiddenWordsCard({ words, r }: {
  words: AuditReport['compliance']['forbidden_words_found']; r: (k: string) => string
}) {
  const grouped = new Map<string, typeof words>()
  for (const fw of words) {
    const loc = fw.location?.toLowerCase() || 'other'
    if (!grouped.has(loc)) grouped.set(loc, [])
    grouped.get(loc)!.push(fw)
  }
  const sortedKeys = [...grouped.keys()].sort((a, b) => {
    const ia = LOCATION_ORDER.indexOf(a)
    const ib = LOCATION_ORDER.indexOf(b)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })

  return (
    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-4">
      <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-3">{r('forbiddenWords')}</h4>
      <div className="space-y-3">
        {sortedKeys.map((loc) => (
          <div key={loc}>
            <span className="text-xs font-semibold text-red-800 dark:text-red-300 uppercase tracking-wide">
              {r(`forbiddenIn${loc.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace(/ /g, '')}`) || loc}
            </span>
            <div className="space-y-1 mt-1">
              {grouped.get(loc)!.map((fw, i) => (
                <p key={i} className="text-sm text-red-700 dark:text-red-400">
                  <span className="font-mono bg-red-100 dark:bg-red-900/30 px-1 rounded">{fw.word}</span>
                  {fw.alternative && <span> — {fw.alternative}</span>}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'pass') return <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
  return <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
}

function statusBg(status: string) {
  if (status === 'pass') return 'bg-green-50/50 dark:bg-green-900/10'
  if (status === 'fail') return 'bg-red-50/50 dark:bg-red-900/10'
  return 'bg-yellow-50/50 dark:bg-yellow-900/10'
}

function severityColor(severity: string) {
  if (severity === 'critical') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  if (severity === 'warning') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
  return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
}
